import { use_store } from "../store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/pro-regular-svg-icons";

interface RedditItem {
  subreddit: string;
  content: string;
  link: string;
  title?: string | null;
}

export default function RedditAnalysis() {
  const { account_analysis, is_elite, t, t_url } = use_store();

  // Get Reddit items from account analysis
  const reddit_items = (account_analysis?.follower_data?.reddit_items ||
    []) as RedditItem[];
  const total_items = account_analysis?.follower_data?.total_items || 0;

  // Handle no red flags found
  if (reddit_items.length === 0) {
    return (
      <div className="w-full max-w-[640px] mx-auto">
        <div className="border-[1px] border-lines rounded-[4px] p-[24px] text-center bg-brand-2/10">
          <p className="bigt text-primary">
            {total_items > 10
              ? t("We couldn't find any red flags on reddit")
              : t(
                  "It looks like {username} might have deleted their reddit history",
                ).replace("{username}", account_analysis?.username || "")}
          </p>
        </div>
      </div>
    );
  }

  // Non-elite user view
  if (!is_elite) {
    return (
      <div className="w-full max-w-[640px] mx-auto">
        <div className="smallt flex flex-row justify-between w-full items-center bg-surface border-lines border-[1px] p-[16px]">
          <div className="flex flex-row gap-[12px] items-center justify-start">
            <FontAwesomeIcon
              className="text-[20px] text-brand-2"
              icon={faWarning}
            />
            <div>
              <p className="bigt">
                {t("Found {number} red flags found").replace(
                  "{number}",
                  String(reddit_items.length + 1),
                )}
              </p>
            </div>
          </div>
          <a
            className="smallt border-[1px] rounded-[4px] px-[16px] py-[12px] border-brand-2 hover:opacity-50 cursor-pointer min-w-[95px] text-center"
            href={t_url("/try")}
          >
            {t("REVEAL ALL")}
          </a>
        </div>
      </div>
    );
  }

  // Elite user view - show all items
  const visible_items = reddit_items;

  return (
    <div className="w-full max-w-[640px] mx-auto">
      <div className="mb-[24px] text-center">
        <p className="stitle text-primary mb-[8px]">
          {t("Reddit Activity Analysis")}
        </p>
        <p className="vsmallt text-secondary">
          {t("{count} red flags found").replace(
            "{count}",
            String(reddit_items.length),
          )}
        </p>
      </div>

      <div className="flex flex-col gap-[12px]">
        {visible_items.map((item, index) => (
          <div
            key={index}
            className="bg-surface border-[1px] border-lines rounded-[4px] p-[20px]"
          >
            {/* Subreddit Badge */}
            <div className="flex items-center gap-[8px] mb-[12px]">
              <div className="py-[4px] px-[8px] bg-red-500/10 border-[1px] border-red-500 rounded-[4px]">
                <p className="eyebrows text-red-500">r/{item.subreddit}</p>
              </div>
            </div>

            {/* Title if it's a post */}
            {item.title && (
              <h3 className="smallt font-semibold text-primary mb-[8px]">
                {item.title}
              </h3>
            )}

            {/* Content */}
            {item.content && (
              <p className="vsmallt text-secondary mb-[12px] line-clamp-3">
                {item.content}
              </p>
            )}

            {/* View on Reddit link */}
            {item.link && (
              <a
                href={
                  item.link.startsWith("http")
                    ? item.link
                    : `https://reddit.com${item.link}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block vsmallt text-brand-2 hover:opacity-70"
              >
                {t("View on Reddit")} →
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
